// api.js — all HTTP calls to the PropTrack backend
const BASE = process.env.REACT_APP_API_URL || "/api";

export function getToken() { return localStorage.getItem("proptrack_token"); }
function authHeaders() { return { Authorization: `Bearer ${getToken()}` }; }

async function request(method, path, body, isFormData = false) {
  const headers = authHeaders();
  if (!isFormData) headers["Content-Type"] = "application/json";
  const res = await fetch(`${BASE}${path}`, {
    method, headers,
    body: isFormData ? body : body ? JSON.stringify(body) : undefined,
  });
  const data = await res.json().catch(() => ({ error: "Server error" }));
  if (res.status === 401) {
    localStorage.removeItem("proptrack_token");
    window.location.reload();
    throw new Error("Session expired — please log in again");
  }
  if (!res.ok) throw new Error(data.error || `HTTP ${res.status}`);
  return data;
}

export const authAPI = {
  initiateEmail:        (email, portalType) => request("POST", "/auth/initiate-email", { email, portalType }),
  verifyEmailToken:     (token)             => request("GET",  `/auth/verify-email?token=${token}`),
  completeRegistration: (form)              => request("POST", "/auth/complete-registration", form),
  contractorInitiate:   (form)              => request("POST", "/auth/contractor/initiate", form),
  login:                (form)              => request("POST", "/auth/login", form),
  me: () => request("GET", "/auth/me").then(data => {
    // If the server issued a refreshed token (e.g. role changed in DB), store it
    if (data._refreshedToken) {
      localStorage.setItem("proptrack_token", data._refreshedToken);
      delete data._refreshedToken;
    }
    return data;
  }),
  updateProfile:        (form)              => request("PATCH","/auth/profile", form),
  logout:               ()                  => request("POST", "/auth/logout"),
};

export const usersAPI = {
  list:            ()         => request("GET",    "/users"),
  listAllied:      (search)   => request("GET",    `/users/allied${search?`?search=${encodeURIComponent(search)}`:""}`),
  create:          (form)     => request("POST",   "/users", form),
  setRole:         (id, role) => request("PATCH",  `/users/${id}/role`, { role }),
  deactivate:      (id)       => request("PATCH",  `/users/${id}/deactivate`, {}),
  activate:        (id)       => request("PATCH",  `/users/${id}/activate`, {}),
  permanentDelete: (id)       => request("DELETE", `/users/${id}`),
};

export const applicationsAPI = {
  submit:      (form)                        => request("POST",  "/applications", form),
  mine:        ()                            => request("GET",   "/applications/mine"),
  list:        (status)                      => request("GET",   `/applications${status?`?status=${status}`:""}`),
  approve:     (id)                          => request("POST",  `/applications/${id}/approve`, {}),
  requestInfo: (id, notes, activateProfile)  => request("POST",  `/applications/${id}/request-info`, { notes, activateProfile }),
};

export const ticketsAPI = {
  list: (status) => request("GET", `/tickets${status?`?status=${encodeURIComponent(status)}`:""}`),
  get:  (id)     => request("GET", `/tickets/${id}`),
  create: (form, photoFile) => {
    const fd = new FormData();
    Object.entries(form).forEach(([k,v]) => fd.append(k, v));
    if (photoFile) fd.append("photo", photoFile);
    return request("POST", "/tickets", fd, true);
  },
  update: (id, form, photoFile) => {
    const fd = new FormData();
    Object.entries(form).forEach(([k,v]) => fd.append(k, v));
    if (photoFile) fd.append("photo", photoFile);
    return request("PATCH", `/tickets/${id}`, fd, true);
  },
  patchFields: (id, fields) => request("PATCH", `/tickets/${id}`, fields),
  setStatus: (id, status) => request("PATCH", `/tickets/${id}/status`, { status }),
  markComplete: (id, { completionNote, completionPhoto, invoiceFile }) => {
    const fd = new FormData();
    if (completionNote) fd.append("completionNote", completionNote);
    fd.append("completionPhoto", completionPhoto);
    if (invoiceFile) fd.append("invoice", invoiceFile);
    return request("POST", `/tickets/${id}/complete`, fd, true);
  },
  uploadInvoice: (id, file) => {
    const fd = new FormData();
    fd.append("invoice", file);
    return request("POST", `/tickets/${id}/invoice`, fd, true);
  },
  downloadInvoice: async (id, filename) => {
    const res = await fetch(`${BASE}/files/invoice/${id}/download`, { headers: authHeaders() });
    if (!res.ok) throw new Error("Download failed");
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = filename || "invoice.pdf"; a.click();
    setTimeout(() => URL.revokeObjectURL(url), 5000);
  },
};

export function fileUrl(fileId, folder = "photos") {
  if (!fileId) return null;
  return `${BASE}/files/${folder}/${fileId}?token=${getToken()}`;
}

export const bidsAPI = {
  submit:   (ticketId, form)                => request("POST",  `/tickets/${ticketId}/bids`, form),
  update:   (ticketId, bidId, form)         => request("PATCH", `/tickets/${ticketId}/bids/${bidId}`, form),
  withdraw: (ticketId, bidId)               => request("PATCH", `/tickets/${ticketId}/bids/${bidId}`, { withdraw:true }),
  action:   (ticketId, bidId, action, note) => request("POST",  `/tickets/${ticketId}/bids/${bidId}/action`, { action, note }),
  assign:   (ticketId, contractorId, note)  => request("POST",  `/tickets/${ticketId}/assign`, { contractorId, note }),
  schedule: (ticketId, scheduledDate)       => request("PATCH", `/tickets/${ticketId}/schedule`, { scheduledDate }),
};

export const questionsAPI = {
  ask:   (ticketId, message)         => request("POST", `/tickets/${ticketId}/questions`, { message }),
  reply: (ticketId, qId, message)    => request("POST", `/tickets/${ticketId}/questions/${qId}/reply`, { message }),
};

export const contractorsAPI = {
  list: () => request("GET", "/contractors"),
};

export const tempContractorsAPI = {
  list:   (search) => request("GET",  `/temp-contractors${search?`?search=${encodeURIComponent(search)}`:""}`),
  create: (form)   => request("POST", "/temp-contractors", form),
};

export const statsAPI = { get: () => request("GET", "/stats") };

export const systemConfigAPI = {
  get:    ()              => request("GET",   "/admin/system-config"),
  update: (emailsEnabled) => request("PATCH", "/admin/system-config", { emailsEnabled }),
};

export const clientsAPI = {
  list:    (search)     => request("GET",   `/clients${search?`?search=${encodeURIComponent(search)}`:""}`),
  get:     (id)         => request("GET",   `/clients/${id}`),
  check:   ({firstName, lastName, excludeId}) => {
    const params = new URLSearchParams();
    if (firstName) params.set("firstName", firstName);
    if (lastName)  params.set("lastName",  lastName);
    if (excludeId) params.set("excludeId", excludeId);
    return request("GET", `/clients/check?${params.toString()}`);
  },
  create:  (form)       => request("POST",  "/clients", form),
  update:  (id, form)   => request("PATCH", `/clients/${id}`, form),
  archive: (id)         => request("DELETE",`/clients/${id}`),
};

export const propertiesAPI = {
  list:     (params={})  => {
    const qs = new URLSearchParams(Object.entries(params).filter(([,v])=>v!==undefined&&v!=="")).toString();
    return request("GET", `/properties${qs?`?${qs}`:""}`);
  },
  get:      (id)         => request("GET",   `/properties/${id}`),
  create:   (form)       => request("POST",  "/properties", form),
  update:   (id, form)   => request("PATCH", `/properties/${id}`, form),
  archive:  (id)         => request("DELETE",`/properties/${id}`),
  regeocode:(id)         => request("POST",  `/properties/${id}/geocode`, {}),
};

export const importAPI = {
  properties: (csvFile) => {
    const fd = new FormData();
    fd.append("csv", csvFile);
    return request("POST", "/import/properties", fd, true);
  },
};

export const mapsAPI = {
  getKey: () => request("GET", "/geocode/key"),
};
